import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import {
  nextRotation,
  rotationForPage,
} from "@Features/pdf-viewer/components/PdfViewer/PdfNewImplementation/pdfRotation";
import type { PageSize, Rotation, RotationDirection } from "@WebTypes/pdf";
import type { PdfDocument } from "@Features/pdf-viewer/components/pdfWorker";

type PdfSessionState = {
  token: number;
  pdfDocument: PdfDocument | null;
  numPages: number;
  pageSizes: PageSize[];
  pageRotations: Record<number, Rotation>;
  isPdfFrozen: boolean;
};

type PublishDocumentPayload = {
  pdfDocument: PdfDocument;
  numPages: number;
  pageSizes: PageSize[];
};

type PdfSessionContextValue = {
  isPrivate: boolean;
  pdfDocument: PdfDocument | null;
  numPages: number;
  pageSizes: PageSize[];
  pageRotations: Record<number, Rotation>;
  isPdfFrozen: boolean;
  setPdfFrozen: (frozen: boolean) => void;
  beginSession: () => number;
  publishDocument: (token: number, payload: PublishDocumentPayload) => void;
  rotatePages: (pageNumbers: number[], direction: RotationDirection) => void;
  registerScrollToPage: (scrollToPage: ((pageNumber: number) => void) | null) => void;
  scrollToPage: (pageNumber: number) => void;
  registerHeldPages: (isPageHeld: ((pageNumber: number) => boolean) | null) => void;
  drainPendingReleases: () => void;
  releasePageWhenFree: (pageNumber: number, release: () => void) => void;
};

const EMPTY_SESSION: PdfSessionState = {
  token: 0,
  pdfDocument: null,
  numPages: 0,
  pageSizes: [],
  pageRotations: {},
  isPdfFrozen: false,
};

const NOT_IN_PROVIDER = (): void => {};

const PdfSessionContext = createContext<PdfSessionContextValue>({
  ...EMPTY_SESSION,
  isPrivate: false,
  beginSession: () => 0,
  setPdfFrozen: NOT_IN_PROVIDER,
  publishDocument: NOT_IN_PROVIDER,
  rotatePages: NOT_IN_PROVIDER,
  registerScrollToPage: NOT_IN_PROVIDER,
  scrollToPage: NOT_IN_PROVIDER,
  registerHeldPages: NOT_IN_PROVIDER,
  drainPendingReleases: NOT_IN_PROVIDER,
  releasePageWhenFree: (_pageNumber: number, release: () => void) => release(),
});

export function PdfSessionProvider({
  isPrivate = false,
  children,
}: Readonly<{ isPrivate?: boolean; children: React.ReactNode }>): React.JSX.Element {
  const [session, setSession] = useState<PdfSessionState>(EMPTY_SESSION);
  const tokenRef = useRef(0);
  const scrollToPageRef = useRef<((pageNumber: number) => void) | null>(null);
  const heldPagesRef = useRef<((pageNumber: number) => boolean) | null>(null);
  const pendingReleasesRef = useRef(new Map<number, () => void>());

  const beginSession = useCallback((): number => {
    tokenRef.current += 1;
    const token = tokenRef.current;
    pendingReleasesRef.current.clear();
    setSession({ ...EMPTY_SESSION, token });
    return token;
  }, []);

  const publishDocument = useCallback((token: number, payload: PublishDocumentPayload): void => {
    setSession((prev) => (prev.token === token ? { ...prev, ...payload } : prev));
  }, []);

  const setPdfFrozen = useCallback((frozen: boolean): void => {
    setSession((prev) => (prev.isPdfFrozen === frozen ? prev : { ...prev, isPdfFrozen: frozen }));
  }, []);

  const rotatePages = useCallback((pageNumbers: number[], direction: RotationDirection): void => {
    if (pageNumbers.length === 0) return;
    setSession((prev) => {
      if (prev.isPdfFrozen) return prev;
      const pageRotations = { ...prev.pageRotations };
      for (const pageNumber of pageNumbers) {
        if (pageNumber < 1 || pageNumber > prev.numPages) continue;
        const rotation = nextRotation(rotationForPage(pageRotations, pageNumber), direction);
        if (rotation === 0) delete pageRotations[pageNumber];
        else pageRotations[pageNumber] = rotation;
      }
      return { ...prev, pageRotations };
    });
  }, []);

  const registerScrollToPage = useCallback((scrollToPage: ((pageNumber: number) => void) | null): void => {
    scrollToPageRef.current = scrollToPage;
  }, []);

  const scrollToPage = useCallback((pageNumber: number): void => {
    scrollToPageRef.current?.(pageNumber);
  }, []);

  const registerHeldPages = useCallback((predicate: ((pageNumber: number) => boolean) | null): void => {
    heldPagesRef.current = predicate;
  }, []);

  const isPageHeld = useCallback((pageNumber: number): boolean => {
    return heldPagesRef.current?.(pageNumber) ?? false;
  }, []);

  const releasePageWhenFree = useCallback(
    (pageNumber: number, release: () => void): void => {
      if (!isPageHeld(pageNumber)) {
        release();
        return;
      }
      pendingReleasesRef.current.set(pageNumber, release);
    },
    [isPageHeld],
  );

  const drainPendingReleases = useCallback((): void => {
    const pending = pendingReleasesRef.current;
    if (pending.size === 0) return;
    for (const [pageNumber, release] of pending) {
      if (isPageHeld(pageNumber)) continue;
      pending.delete(pageNumber);
      release();
    }
  }, [isPageHeld]);

  const value = useMemo(
    () => ({
      isPrivate,
      pdfDocument: session.pdfDocument,
      numPages: session.numPages,
      pageSizes: session.pageSizes,
      pageRotations: session.pageRotations,
      isPdfFrozen: session.isPdfFrozen,
      setPdfFrozen,
      beginSession,
      publishDocument,
      rotatePages,
      registerScrollToPage,
      scrollToPage,
      registerHeldPages,
      drainPendingReleases,
      releasePageWhenFree,
    }),
    [
      isPrivate,
      session,
      setPdfFrozen,
      beginSession,
      publishDocument,
      rotatePages,
      registerScrollToPage,
      scrollToPage,
      registerHeldPages,
      drainPendingReleases,
      releasePageWhenFree,
    ],
  );

  return <PdfSessionContext.Provider value={value}>{children}</PdfSessionContext.Provider>;
}

export function usePdfSession(): PdfSessionContextValue {
  return useContext(PdfSessionContext);
}
